import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AxiosResponse } from "axios";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { Utility } from "../../core/Utility";
import { DateInput } from "../../components/DateInput/DateInput";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { PdfPreview } from "../../components/PdfPreview/PDFPreview";
import { getProductQuantityByDayReportTemplate } from "../../document-templates/ProductQuantityByDayReportTemplate";
import { ProductOverview, ProductType } from "../../models/Product";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { useApplicationContext } from "../../application/ApplicationContext";
import { getAllDocuments } from "../../services/NewVersionService";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllProducts } from "../../services/ProductService";
import "./ProductQuantityByDayReportPage.scss";

export const ProductQuantityByDayReportPage = () => {
  const { t } = useTranslation();

  const { state } = useApplicationContext();
  const businessYear = state.businessYear ?? new Date().getFullYear();
  const yearStart = new Date(businessYear, 0, 1);
  const yearEnd = new Date(businessYear, 11, 31);

  const getDefaultDateForBusinessYear = () => {
    const today = new Date();
    if (businessYear === today.getFullYear()) {
      return today; // za tekuću poslovnu godinu
    }
    return yearEnd; // za ostale – 31.12. te godine
  };
  const [date, setDate] = useState<Date>(getDefaultDateForBusinessYear());

  const [selectedWarehouse, setSelectedWarehouse] = useState<string>("");
  const [mercantileOverview, setMercantileOverview] = useState<
    ProductOverview[]
  >([]);
  const [repromaterialOverview, setRepromaterialOverview] = useState<
    ProductOverview[]
  >([]);

  const [processing, setProcessing] = useState(false);
  const [warehousesApi, setWarehousesApi] = useState<any[]>([]);
  const [productsApi, setProductsApi] = useState<any[]>([]);

  const [pdfData, setPdfData] = useState<any>(
    getProductQuantityByDayReportTemplate(
      mercantileOverview,
      repromaterialOverview,
      selectedWarehouse,
      date
    )
  );
  const downloadPdfDocument = useRef(null);
  const printPdfDocument = useRef(null);

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  const refreshLookups = async () => {
    const [wResp, pResp]: [AxiosResponse, AxiosResponse] = await Promise.all([
      getAllWarehouses(),
      getAllProducts(),
    ]);
    [wResp, pResp].forEach(ensureSuccess);
    setWarehousesApi(wResp?.data?.result ?? []);
    setProductsApi(pResp?.data?.result ?? []);
  };

  useEffect(() => {
    setProcessing(true);
    refreshLookups().finally(() => setProcessing(false));
  }, []);

  useEffect(() => {
    setDate(getDefaultDateForBusinessYear());
  }, [businessYear]);

  useEffect(() => {
    if (warehousesApi.length === 0 || productsApi.length === 0) return;

    const run = async () => {
      setProcessing(true);
      try {
        setMercantileOverview([]);
        setRepromaterialOverview([]);
        await formatMercantileOverviewData();
        await formatRepromaterialOverviewData();
      } finally {
        setProcessing(false);
      }
    };

    run();
  }, [warehousesApi.length, productsApi.length, selectedWarehouse, date]);

  // populate pdf when data changes
  useEffect(() => {
    const newPdf = getProductQuantityByDayReportTemplate(
      mercantileOverview,
      repromaterialOverview,
      (warehousesApi.find((item) => item.id === selectedWarehouse)
        ?.name as string) ?? " - Svi",
      date
    );
    setPdfData(newPdf);
  }, [mercantileOverview, repromaterialOverview, selectedWarehouse, date]);

  const formatMercantileOverviewData = async () => {
    const warehouse = warehousesApi.find(
      (item) => item.id == selectedWarehouse
    );
    const warehouseName = warehouse?.name;

    const dateFrom = yearStart; // 01.01. poslovne godine
    const dateTo = date; // lokalni filter "na dan"
    const dateFromFormatted = new Date(dateFrom).getTime();
    const dateToFormatted = new Date(dateTo).getTime() + 1000 * 60 * 60 * 24;

    let copyOfMercantileOverview: ProductOverview[] = [];

    // prijemnice i otpremnice merkantile
    const [inputsResp, outputsResp]: [AxiosResponse, AxiosResponse] =
      await Promise.all([
        getAllDocuments(true, true, dateFrom, dateTo), // merkantila - prijemnice
        getAllDocuments(true, false, dateFrom, dateTo), // merkantila - otpremnice
      ]);

    const inputDocs = inputsResp.data.result;
    const outputDocs = outputsResp.data.result;

    // ULAZI
    for (const mercantileInput of inputDocs) {
      if (mercantileInput?.isDocumentFinished !== true) continue;

      const ts = new Date(mercantileInput.date).getTime();
      if (ts < dateFromFormatted || ts > dateToFormatted) continue;

      if (
        selectedWarehouse &&
        mercantileInput.warehouseName !== warehouseName
      ) {
        continue;
      }

      const productId = mercantileInput.productId;
      if (!productId) continue;

      const productMeta = productsApi.find((p) => p.id == productId);
      if (!productMeta) continue;

      const index = copyOfMercantileOverview.findIndex(
        (m) => m.product.id === productMeta.id
      );

      if (index >= 0) {
        copyOfMercantileOverview[index].amount =
          (copyOfMercantileOverview[index].amount ?? 0) +
          (mercantileInput.netto ?? 0);
      } else {
        copyOfMercantileOverview.push({
          id: mercantileInput.id,
          code: productMeta.code ?? "",
          name: productMeta.name as string,
          year: undefined,
          type: ProductType.MercantileGoods,
          product: productMeta,
          mark: productMeta.measuringUnit,
          amount: mercantileInput.netto ?? 0,
        });
      }
    }

    // IZLAZI
    for (const mercantileOutput of outputDocs) {
      if (mercantileOutput?.isDocumentFinished !== true) continue;

      const ts = new Date(mercantileOutput.date).getTime();
      if (ts < dateFromFormatted || ts > dateToFormatted) continue;

      if (
        selectedWarehouse &&
        mercantileOutput.warehouseName !== warehouseName
      ) {
        continue;
      }

      const productId = mercantileOutput.productId;
      if (!productId) continue;

      const productMeta = productsApi.find((p) => p.id == productId);
      if (!productMeta) continue;

      const index = copyOfMercantileOverview.findIndex(
        (m) => m.product.id === productMeta.id
      );

      if (index >= 0) {
        copyOfMercantileOverview[index].amount =
          (copyOfMercantileOverview[index].amount ?? 0) -
          (mercantileOutput.netto ?? 0);
      } else {
        copyOfMercantileOverview.push({
          id: mercantileOutput.id,
          code: productMeta.code ?? "",
          name: productMeta.name as string,
          year: undefined,
          type: ProductType.MercantileGoods,
          product: productMeta,
          mark: productMeta.measuringUnit,
          amount: (mercantileOutput.netto ?? 0) * -1,
        });
      }
    }
    const sortedMercantileOverview = copyOfMercantileOverview.sort(
      (a: ProductOverview, b: ProductOverview) => (a?.name > b?.name ? 1 : -1)
    );
    setMercantileOverview(sortedMercantileOverview);
  };

  const formatRepromaterialOverviewData = async () => {
    const warehouse = warehousesApi.find(
      (item) => item.id === selectedWarehouse
    );
    const warehouseName = warehouse?.name;

    const dateFrom = yearStart; // 01.01. poslovne godine
    const dateTo = date; // lokalni "na dan"
    const dateFromFormatted = new Date(dateFrom).getTime();
    const dateToFormatted = new Date(dateTo).getTime() + 1000 * 60 * 60 * 24;

    let copyOfRepromaterialOverview: ProductOverview[] = [];

    const [inputsResp, outputsResp]: [AxiosResponse, AxiosResponse] =
      await Promise.all([
        getAllDocuments(false, true, dateFrom, dateTo), // repromaterijal - prijemnice
        getAllDocuments(false, false, dateFrom, dateTo), // repromaterijal - otpremnice
      ]);

    const repromaterialInputs = inputsResp.data.result;
    const repromaterialOutputs = outputsResp.data.result;

    const processReproDocument = (doc: any, sign: 1 | -1) => {
      if (doc?.isDocumentFinished !== true) return;

      const ts = new Date(doc.date).getTime();
      if (ts < dateFromFormatted || ts > dateToFormatted) return;

      const warehouseMeta = warehousesApi.find((w) => w.id === doc.warehouseId);
      if (warehouseMeta?.virtual === true) return;

      if (selectedWarehouse && doc.warehouseName !== warehouseName) {
        return;
      }

      const productIds = (doc.reproProducts ?? "")
        .split(";")
        .map((s: string) => s.trim())
        .filter((s: string) => s !== "");

      const amounts = (doc.reproAmounts ?? "")
        .split(";")
        .map((s: string) => s.trim())
        .filter((s: string) => s !== "");

      productIds.forEach((prodId: string, idx: number) => {
        const rawAmount = amounts[idx] ?? "0";
        const parsed = parseFloat(rawAmount); // "10.00kg" -> 10
        const amount = (isNaN(parsed) ? 0 : parsed) * sign;

        const productMeta = productsApi.find(
          (p) => p.id?.toString() === prodId.toString()
        );
        if (!productMeta) return;

        const index = copyOfRepromaterialOverview.findIndex(
          (r) => r.product.id === productMeta.id
        );

        if (index >= 0) {
          copyOfRepromaterialOverview[index].amount =
            (copyOfRepromaterialOverview[index].amount ?? 0) + amount;
        } else {
          copyOfRepromaterialOverview.push({
            id: productMeta.id,
            code: productMeta.code ?? "",
            name: productMeta.name as string,
            year: undefined,
            type: ProductType.RawMaterial,
            product: productMeta,
            mark: productMeta.measuringUnit,
            amount,
          });
        }
      });
    };

    // ULAZI
    for (const doc of repromaterialInputs) {
      processReproDocument(doc, 1);
    }

    // IZLAZI
    for (const doc of repromaterialOutputs) {
      processReproDocument(doc, -1);
    }

    const filteredCopyOfRepromaterialOverview =
      copyOfRepromaterialOverview.filter(
        (oneReprom) => oneReprom.code !== "" && oneReprom.code !== undefined
      );
    const sortedRepromaterialOverview =
      filteredCopyOfRepromaterialOverview.sort(
        (a: ProductOverview, b: ProductOverview) => (a?.name > b?.name ? 1 : -1)
      );
    setRepromaterialOverview(sortedRepromaterialOverview);
  };
  console.log(repromaterialOverview);
  const onSelectedWarehouseChange = (property: string, value: string) => {
    setSelectedWarehouse(value);
  };

  const onDateChange = (property: string, value: Date | null) => {
    setDate(value as Date);
  };

  const renderMercantileHeader = () => {
    return (
      <div className="mercantile-header">
        <span>{t("MERKANTILNA ROBA")}</span>
      </div>
    );
  };

  const renderRepromaterialHeader = () => {
    return (
      <div className="repromaterial-header">
        <span>{t("REPROMATERIJAL")}</span>
      </div>
    );
  };

  const typeOfProduct = (rowData: any) => {
    return rowData.type == ProductType.MercantileGoods
      ? t("Merkantila")
      : t("Repromaterijal");
  };

  const formatMark = (rowData: any) => {
    return productsApi.find((item) => item.id === rowData.product.id)
      ?.measuringUnit;
  };

  const categoryOfProduct = (rowData: any) => {
    return productsApi.find((item) => item.id === rowData.product.id)?.category
      ?.name;
  };

  const amountOfReproProduct = (rowData: any) => {
    return !Number.isInteger(rowData.amount)
      ? rowData.amount.toFixed(3) == 0 ||
        rowData.amount.toFixed(3) == 0.001 ||
        rowData.amount.toFixed(3) == -0.001
        ? "0"
        : rowData.amount.toFixed(3)
      : rowData.amount.toFixed(0);
  };

  const amount = (rowData: ProductOverview) => {
    return Utility.addThousandSeparator(rowData?.amount as number);
  };

  const {
    control,
    formState: { errors },
  } = useForm({
    mode: "all",
  });

  const exportToExcel = () => {
    const excelMercantileObject = mercantileOverview.map(
      (item: ProductOverview) => {
        return {
          SifraRobe: item?.code ?? "",
          Naziv: item?.name ?? "",
          Tip: "Merkantila",
          VrstaRobe: "/",
          Oznaka:
            productsApi.find(
              (oneProduct) => oneProduct.id === item?.product?.id
            )?.measuringUnit ?? "",
          Kolicina: item.amount ?? "",
        };
      }
    );
    const excelRepromaterialObject = repromaterialOverview.map(
      (item: ProductOverview) => {
        const amount = item.amount ?? 0;
        const formattedAmount = Number.isInteger(amount)
          ? amount
          : Math.abs(amount) < 0.001
          ? 0
          : amount;

        return {
          SifraRobe: item?.code ?? "",
          Naziv: item?.name ?? "",
          Tip: "Repromaterijal",
          VrstaRobe:
            productsApi.find(
              (oneProduct) => oneProduct.id === item?.product?.id
            )?.category?.name ?? "",
          Oznaka:
            productsApi.find(
              (oneProduct) => oneProduct.id === item?.product?.id
            )?.measuringUnit ?? "",
          Kolicina: formattedAmount,
        };
      }
    );
    let excelObject: any[] = [];
    const emptyRow = {
      SifraRobe: "",
      Naziv: "",
      Tip: "",
      VrstaRobe: "",
      Oznaka: "",
      Kolicina: "",
    };
    excelObject = [
      emptyRow,
      ...excelMercantileObject,
      emptyRow,
      ...excelRepromaterialObject,
    ];
    import("xlsx").then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(excelObject);
      if (worksheet["!ref"]) {
        const range = xlsx.utils.decode_range(worksheet["!ref"]);
        for (let i = range.s.r + 1; i <= range.e.r; i++) {
          const cellAddress = xlsx.utils.encode_cell({ r: i, c: 5 }); // Kolona F (Kolicina)
          if (
            worksheet[cellAddress] &&
            typeof worksheet[cellAddress].v === "number"
          ) {
            worksheet[cellAddress].t = "n"; // Numerički tip
            // Dinamički format u zavisnosti od toga da li je ceo broj
            if (Number.isInteger(worksheet[cellAddress].v)) {
              worksheet[cellAddress].z = "0"; // Format za cele brojeve
            } else {
              worksheet[cellAddress].z = "0.00"; // Format za decimale
            }
          }
        }
      }
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      saveAsExcelFile(
        excelBuffer,
        `Stanje robe na dan ${Utility.formatDate(new Date())}`
      );
    });
  };

  const saveAsExcelFile = (buffer: any, fileName: any) => {
    import("file-saver").then((module: any) => {
      if (module && module.default) {
        let EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        let EXCEL_EXTENSION = ".xlsx";
        const data = new Blob([buffer], {
          type: EXCEL_TYPE,
        });
        module.default.saveAs(data, fileName + EXCEL_EXTENSION);
      }
    });
  };

  return (
    <PageLayout>
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-shop-window header-title-icon"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
              }}
            ></i>
            <span className="header-title">{t("Stanje robe na dan")}</span>
            <DateInput
              id="date"
              value={date}
              onValueChange={onDateChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements">{t("u magacinu")}</span>
            <DropdownInput
              id="selectedWarehouse"
              value={selectedWarehouse}
              onValueChange={onSelectedWarehouseChange}
              options={warehousesApi.filter((item) => item.virtual !== true)}
              optionValue="id"
              optionLabel="name"
              labelText=""
              placeholderText={t("Magacin")}
              control={control}
              errors={errors}
              disabled={processing}
            />
          </div>
          <div className="header-right">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={""}
              icon="bi bi-file-earmark-pdf"
              onButtonClick={() => {
                if (downloadPdfDocument.current) {
                  // @ts-ignore
                  downloadPdfDocument.current();
                }
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
              }}
              labelText={""}
              icon="bi bi-printer"
              onButtonClick={() => {
                if (printPdfDocument.current) {
                  // @ts-ignore
                  printPdfDocument.current();
                }
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                marginLeft: "5px",
              }}
              labelText={""}
              icon="bi bi-file-earmark-excel"
              onButtonClick={exportToExcel}
            />
          </div>
        </div>
        <div className="table-body">
          {processing ? (
            <ProgressSpinner
              style={{ width: "100px", height: "100px", color: "green" }}
              strokeWidth="4"
              animationDuration=".8s"
            />
          ) : (
            <DataTable
              header={renderMercantileHeader}
              value={mercantileOverview}
              showGridlines
              stripedRows
            >
              <Column field="code" header={t("Šifra robe")} />
              <Column field="name" header={t("Naziv proizvoda")} />
              <Column body={typeOfProduct} header={t("Tip robe")} />
              <Column field="mark" header={t("Oznaka")} />
              <Column body={amount} header={t("Količina")} />
            </DataTable>
          )}
        </div>
        <div className="table-body-repromaterial">
          {!processing && (
            <DataTable
              header={renderRepromaterialHeader}
              value={repromaterialOverview}
              showGridlines
              stripedRows
            >
              <Column field="code" header={t("Šifra robe")} />
              <Column field="name" header={t("Naziv proizvoda")} />
              <Column body={typeOfProduct} header={t("Tip robe")} />
              <Column body={categoryOfProduct} header={t("Vrsta robe")} />
              <Column body={formatMark} header={t("Oznaka")} />
              <Column body={amountOfReproProduct} header={t("Količina")} />
            </DataTable>
          )}
        </div>
        <div className="document-preview">
          <PdfPreview
            pdfDefinition={pdfData}
            downloadDocument={downloadPdfDocument}
            printDocument={printPdfDocument}
            documentName={`${t("Stanje robe na dan u")} ${
              warehousesApi.find((item) => item.id === selectedWarehouse)?.name
            }_${Utility.formatDate(new Date())}.pdf`}
          />
        </div>
      </div>
    </PageLayout>
  );
};
